import {MeterButton} from '@github-ui/copilot-chat/components/MeterButton'
import {ChatComposer, type ComposerAttachment} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {RepoPicker} from '@github-ui/agent-sessions-interface/components/RepoPicker'
import {useCallback, useImperativeHandle, useMemo, useRef} from 'react'
import {clsx} from 'clsx'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {ImageReference} from '@github-ui/copilot-chat-types'
import {FilePickerPanel} from '@github-ui/copilot-shared-ui/components/FilePickerPanel'
import {fetchRepos} from '@github-ui/copilot-shared-ui/repos/fetch-repos'

import {
  EnvironmentAttachmentControls,
  EnvironmentImageAttachments,
} from '@github-ui/one-copilot-composer/EnvironmentAttachments'
import {ImageReferenceToken} from '@github-ui/one-copilot-composer/ImageReferenceToken'
import {ModelPicker} from '@github-ui/one-copilot-composer/ModelPicker'
import {ReasoningEffortPicker} from '@github-ui/one-copilot-composer/ReasoningEffortPicker'
import {ComposerToolbar} from '@github-ui/one-copilot-composer/ComposerToolbar'
import {EnvironmentStrip} from '@github-ui/one-copilot-composer/environment-strip/EnvironmentStrip'
import type {TaskComposerProps} from '@github-ui/one-copilot-composer/shared'
import {useMarkdownImageHandling} from '@github-ui/one-copilot-composer/use-markdown-image-handling'
import {useTaskComposer, useTaskComposerTargetState} from '@github-ui/one-copilot-composer/use-task-composer'
import {
  isSandboxBranchSelectorDisabled,
  isSessionTokenMeterApplicable,
} from '@github-ui/one-copilot-composer/use-environment-selection'
import styles from './TaskComposer.module.css'
import {MAX_AGENT_TASK_PROMPT_LENGTH} from '@github-ui/copilot-shared-ui/composer/max-prompt-length'
import {QuotaBanner} from '../task-view/QuotaBanner'
import {ChronicleAutocomplete} from '../chronicle-autocomplete/ChronicleAutocomplete'
import {ScopePicker} from './ScopePicker'
import {SpacePicker} from './SpacePicker'

const REFERENCE_ATTACHMENT_ID = 'reference'

function ImageAttachmentPreview({references}: {references: ImageReference[]}) {
  return (
    <ul className={styles.imageAttachments} aria-label="Attached images">
      {references.map(reference => (
        <li key={reference.id}>
          <ImageReferenceToken reference={reference} size="small" />
        </li>
      ))}
    </ul>
  )
}

export function TaskComposer(props: TaskComposerProps) {
  const {
    hideRepoSelector,
    chatInputRef,
    textAreaRef: textAreaRefFromProps,
    className,
    shouldClearTextOnSubmit = true,
    submitWithModifier = true,
    disableWhileSubmitting = false,
    isNewConversation,
    agentQuotaSnapshot,
    initialBranch,
    maxTextareaHeight,
  } = props

  const targetState = useTaskComposerTargetState()
  const images = useMarkdownImageHandling({
    textAreaRef: targetState.textAreaRef,
    repositoryId: targetState.selectedRepo?.id?.toString(),
  })

  const controller = useTaskComposer(props, {targetState, images})

  const {
    textAreaRef,
    setCurrentPrompt,
    currentPrompt,
    handleTextChange,
    handleChronicleSelect,
    setChronicleDismissed,
    chronicleSuggestions,
    onSubmit,
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isImageUploading,
    isBlockedByQuota,
    isAhpEnvironment,
    isSandboxEnvironment,
    isEnvironmentSubmitting,
    isSelectedEnvironmentSubmissionAvailable,
    isRepositorySelectionValid,
    targetSelection,
    selectedEnvironment,
    showCreateTaskStrip,
    showEnvironmentPicker,
    showSandboxOption,
    selectedBranch,
    setSelectedBranch,
    initialNameWithOwner,
    hasSpecifiedRepository,
    branchParam,
    setSelectedEnvironment,
    scopeOwner,
    handleTargetSelection,
    resolvedUserLogin,
    selectedScope,
    setSelectedScope,
    availableOrganizations,
    currentUserAvatarUrl,
    reference,
    setReference,
    chatReference,
    imageReferences,
    shouldShowModelPicker,
    selectedModelId,
    selectModel,
    isModelSelectionReady,
    vendorFilters,
    isTokenBasedBillingEnabled,
    isReasoningEffortEnabled,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
    supportedReasoningEfforts,
    environmentAttachments,
    canPrepareChat,
    selectedSpace,
    setSelectedSpace,
    isSpaceResolving,
    setIsSpaceResolving,
  } = controller

  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
    textAreaRefFromProps ?? null,
    () => textAreaRef.current,
  )
  useImperativeHandle(chatInputRef, () => ({setText: setCurrentPrompt}), [setCurrentPrompt])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachButtonRef = useRef<HTMLButtonElement>(null)
  const openFilePicker = useCallback(() => fileInputRef.current?.click(), [])

  const attachments = useMemo<ComposerAttachment[] | undefined>(() => {
    const combined: ComposerAttachment[] = []
    if (chatReference && reference) {
      const label = reference.range
        ? `${reference.path}#L${reference.range.start}-L${reference.range.end}`
        : reference.path
      combined.push({
        id: REFERENCE_ATTACHMENT_ID,
        label,
        icon: reference.range ? 'snippet' : 'file',
        ...(chatReference.url ? {url: chatReference.url} : {}),
      })
    }
    if (isAhpEnvironment) combined.push(...environmentAttachments.attachments)
    return combined.length > 0 ? combined : undefined
  }, [chatReference, reference, isAhpEnvironment, environmentAttachments.attachments])

  const handleRemoveAttachment = useCallback(
    (id: string) => {
      if (id === REFERENCE_ATTACHMENT_ID) {
        setReference(null)
        return
      }
      environmentAttachments.handleRemoveAttachment(id)
    },
    [setReference, environmentAttachments],
  )

  const showTrailingModelControl = !showCreateTaskStrip && shouldShowModelPicker
  const isChatTarget = targetSelection.kind === 'chat'
  // A Sandbox session spends the same premium quota whichever destination the
  // draft targets, so the meter follows the environment, not the target.
  const showSessionMeter =
    showCreateTaskStrip && isSessionTokenMeterApplicable({isTokenBasedBillingEnabled, isSandboxEnvironment})
  const showTrailingMeter = isTokenBasedBillingEnabled && !isAhpEnvironment && !showCreateTaskStrip

  return (
    <div className={styles.inputWrapper}>
      <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
      <div className={styles.autocompleteWrapper}>
        <ChronicleAutocomplete
          suggestions={chronicleSuggestions}
          onSelect={handleChronicleSelect}
          onDismiss={() => setChronicleDismissed(true)}
          textAreaRef={textAreaRef}
          currentText={currentPrompt}
        />
        <div className={clsx(className, isDraggedOver && styles.isDraggedOver)} {...dropTargetProps}>
          <ChatComposer
            value={currentPrompt}
            onValueChange={handleTextChange}
            textAreaRef={textAreaRef}
            onSubmit={onSubmit}
            placeholder={isChatTarget ? 'Send a message' : 'Give Copilot a background task to work on'}
            ariaLabel={isChatTarget ? 'Send a message' : 'Coding task prompt'}
            containerClassName={clsx(
              styles.composerMinHeight,
              isNewConversation && styles.composerNewConversationMinHeight,
            )}
            inputTextSize="responsive"
            submitWithModifier={isSandboxEnvironment ? false : submitWithModifier}
            clearOnSubmit={shouldClearTextOnSubmit}
            disableWhileSubmitting={disableWhileSubmitting || isAhpEnvironment}
            maxLength={MAX_AGENT_TASK_PROMPT_LENGTH}
            maxTextareaHeight={maxTextareaHeight}
            onPaste={isAhpEnvironment ? environmentAttachments.handlePaste : onPaste}
            {...(attachments ? {attachments} : {})}
            hasAttachedContent={isAhpEnvironment && environmentAttachments.hasAttachments}
            {...(isAhpEnvironment
              ? {
                  onFilesAdded: environmentAttachments.handleFilesAdded,
                  onAttachFiles: environmentAttachments.openFilePicker,
                  onUploadFiles: openFilePicker,
                  onAttachRepo: environmentAttachments.openRepoPicker,
                  attachButtonRef,
                  ...(environmentAttachments.images.length > 0
                    ? {attachmentPreview: <EnvironmentImageAttachments controller={environmentAttachments} />}
                    : {}),
                }
              : imageReferences.length > 0
                ? {attachmentPreview: <ImageAttachmentPreview references={imageReferences} />}
                : {})}
            onRemoveAttachment={handleRemoveAttachment}
            disabled={
              isBlockedByQuota ||
              isEnvironmentSubmitting ||
              !isRepositorySelectionValid ||
              (isChatTarget && !canPrepareChat) ||
              isImageUploading ||
              (isAhpEnvironment
                ? !selectedEnvironment?.environmentId || !isSelectedEnvironmentSubmissionAvailable
                : !isModelSelectionReady || isImageUploading) ||
              isSpaceResolving
            }
            attachMenuContent={
              isChatTarget ? (
                <SpacePicker
                  selectedSpace={selectedSpace}
                  onSelectSpace={setSelectedSpace}
                  isResolving={isSpaceResolving}
                  onResolvingChange={setIsSpaceResolving}
                />
              ) : undefined
            }
            toolbarContent={<ComposerToolbar controller={controller} />}
            trailingToolbarContent={
              showTrailingModelControl || showTrailingMeter || showSessionMeter ? (
                <>
                  {showTrailingModelControl && (
                    <>
                      <div className={styles.pickerButtonWrapper}>
                        <ModelPicker
                          selectedModelId={selectedModelId}
                          onUpdateModel={selectModel}
                          vendorFilters={vendorFilters}
                          isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                        />
                      </div>
                      {isReasoningEffortEnabled && (
                        <ReasoningEffortPicker
                          selectedReasoningEffort={selectedReasoningEffort}
                          onUpdateReasoningEffort={setSelectedReasoningEffort}
                          supportedReasoningEfforts={supportedReasoningEfforts}
                          triggerClassName={styles.pickerButton}
                        />
                      )}
                    </>
                  )}
                  {(showTrailingMeter || showSessionMeter) && <MeterButton />}
                </>
              ) : undefined
            }
          />
        </div>
      </div>
      {isAhpEnvironment && (
        <>
          <EnvironmentAttachmentControls controller={environmentAttachments} fileInputRef={fileInputRef} />
          {environmentAttachments.filePickerOpen && (
            <FilePickerPanel
              isOpen
              onClose={environmentAttachments.closeFilePicker}
              onSelect={environmentAttachments.handleFilePickerSelect}
              repositoryLoader={fetchRepos}
            />
          )}
          <RepoPicker
            anchorRef={attachButtonRef}
            repositoryLoader={fetchRepos}
            selectedRepos={environmentAttachments.selectedRepos}
            onUpdateRepos={environmentAttachments.handleUpdateRepos}
            triggerOpen={environmentAttachments.repoPickerOpen}
            onClose={environmentAttachments.closeRepoPicker}
            anchorSide="outside-top"
          />
        </>
      )}
      {showCreateTaskStrip && (
        <EnvironmentStrip
          hideRepoSelector={hideRepoSelector}
          initialRepoNWO={initialNameWithOwner}
          initialBranch={initialBranch ?? branchParam ?? undefined}
          targetSelection={targetSelection}
          onTargetSelectionChange={handleTargetSelection}
          selectedBranch={selectedBranch}
          onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
          currentPrompt={currentPrompt}
          currentUserLogin={resolvedUserLogin}
          selectedEnvironment={selectedEnvironment}
          onSelectEnvironment={setSelectedEnvironment}
          includeSandboxOption={showSandboxOption}
          allowChat={canPrepareChat && !hasSpecifiedRepository}
          showEnvironmentPicker={showEnvironmentPicker}
          branchSelectorDisabled={isSandboxBranchSelectorDisabled(isSandboxEnvironment)}
          disabled={isEnvironmentSubmitting}
          scopePicker={
            // The scope belongs to the Sandbox environment: it decides which
            // account owns the sandbox and which repositories the picker
            // offers. Selecting Sandbox switches the destination to Chat on its
            // own, so gating this on the Chat launcher hid the picker for the
            // repoless Chat draft that Sandbox actually opens on.
            copilotFeatureFlags.chatOrgScopePicker &&
            isSandboxEnvironment &&
            selectedScope &&
            resolvedUserLogin &&
            availableOrganizations.length > 0 ? (
              <ScopePicker
                selectedScope={selectedScope}
                onSelectScope={setSelectedScope}
                organizations={availableOrganizations}
                currentUserLogin={resolvedUserLogin}
                currentUserAvatarUrl={currentUserAvatarUrl}
                forceIconOnly
                disabled={isEnvironmentSubmitting}
              />
            ) : undefined
          }
          scopeOwner={scopeOwner}
        />
      )}
    </div>
  )
}
